import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../config/firebaseConfig";
import {
  getAcademicYearLabel,
  normalizeAcademicYear,
} from "../utils/academicYear";
import { printReceiptPdf } from "../utils/receiptPdf";
import { generateTransactionDocumentId } from "../utils/transactionId";
import NewReceiptPrintButton from "./receipt/NewReceiptPrintButton";
import { getStudentByUid } from "../services/studentService";

const PAYMENT_MODES = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque"];
const DISCOUNT_REASONS = [
  "Sibling Concession (3 siblings enrolled)",
  "Single Parent (Mother)",
  "Management",
];
const DEFAULT_ACADEMIC_YEAR = "2026-2027";
const DEFAULTS = {
  monthlyTuitionFee: 0,
  monthlyMaintenanceFee: 0,
  monthlySportsFee: 0,
  monthlyComputerFee: 0,
  monthlyFeeMonths: 12,
  otherAnnualCharge: 0,
  otherIdCharge: 0,
  otherDiaryCharge: 0,
  otherExamFee: 0,
  transportFeePerMonth: 0,
  transportMonths: 11,
};

function safeClassName(v) {
  return String(v || "").trim();
}
function normalizeUid(v) {
  return String(v || "").trim();
}

function toNumber(v) {
  const n = Number(
    String(v ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(n) ? n : 0;
}

function hasValue(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function startsWithRomanNumeral(value) {
  const token = String(value || "")
    .trim()
    .split(/[\s/_-]+/)[0];
  return Boolean(token) && /^[IVX]+(?:st|nd|rd|th)?$/i.test(token);
}

function formatClassLabel(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("class ") || lower.startsWith("grade ")) return trimmed;
  if (startsWithRomanNumeral(trimmed) || /^\d/.test(trimmed)) {
    return `Class ${trimmed}`;
  }
  return trimmed;
}

function normalizeCustomCharges(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === "string") {
        const label = item.trim();
        if (!label) return null;
        return { label, amount: "0" };
      }
      const label = String(
        item?.label ?? item?.name ?? item?.fieldName ?? "",
      ).trim();
      if (!label) return null;
      const amountValue = hasValue(item?.amount)
        ? item.amount
        : hasValue(item?.value)
          ? item.value
          : 0;
      return { label, amount: String(toNumber(amountValue)) };
    })
    .filter(Boolean);
}

function mergeCustomCharges(structureCharges, paymentCharges) {
  const base = normalizeCustomCharges(structureCharges);
  const overrides = normalizeCustomCharges(paymentCharges);
  const overrideMap = new Map(
    overrides.map((item) => [item.label.toLowerCase(), item]),
  );
  const used = new Set();
  const merged = base.map((item) => {
    const key = item.label.toLowerCase();
    const override = overrideMap.get(key);
    if (override) {
      used.add(key);
      return { label: item.label, amount: override.amount };
    }
    used.add(key);
    return item;
  });
  overrides.forEach((item) => {
    const key = item.label.toLowerCase();
    if (!used.has(key)) merged.push(item);
  });
  return merged;
}

function customChargesPayload(list) {
  return normalizeCustomCharges(list).map((item) => ({
    label: item.label,
    amount: toNumber(item.amount),
  }));
}

async function getCurrentUserInfo() {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) return null;

  try {
    const usersQuery = query(
      collection(db, "users"),
      where("uid", "==", currentUser.uid),
    );
    const snapshot = await getDocs(usersQuery);
    const document = snapshot.docs[0];

    if (!document) {
      return {
        uid: currentUser.uid,
        email: currentUser.email || "",
        displayName: currentUser.displayName || "",
        photoURL: currentUser.photoURL || "",
      };
    }

    const data = document.data() || {};
    return {
      id: document.id,
      uid: data.uid || currentUser.uid,
      email: data.email || currentUser.email || "",
      displayName:
        data.displayName ||
        data.fullName ||
        data.name ||
        currentUser.displayName ||
        "",
      photoURL:
        data.photoURL ||
        data.photoUrl ||
        data.profileImage ||
        currentUser.photoURL ||
        "",
      role: data.role || data.roles || null,
    };
  } catch (error) {
    console.error("Unable to load current user info:", error);
    return {
      uid: currentUser.uid,
      email: currentUser.email || "",
      displayName: currentUser.displayName || "",
      photoURL: currentUser.photoURL || "",
    };
  }
}

function pick(record, keys, fallback = "") {
  for (const k of keys) {
    if (record && hasValue(record[k])) return record[k];
  }
  return fallback;
}

function pickNum(record, keys, fallback = 0) {
  return toNumber(pick(record, keys, fallback));
}

function parseDateValue(v) {
  if (!v) return "";
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      const [dd, mm, yyyy] = v.split("/");
      return `${yyyy}-${mm}-${dd}`;
    }
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  if (typeof v === "object" && v?.seconds) {
    return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  }
  return "";
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(`${v}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function money(v) {
  return `INR ${toNumber(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function buildPaymentDocId(uid, className) {
  const safeUid = normalizeUid(uid) || "unknown";
  const safeClass = safeClassName(className)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return `${safeUid}__${safeClass || "unknown"}`;
}

function classKey(className) {
  return safeClassName(className)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

function buildFeeStructureKey(academicYear, className) {
  const yearKey = normalizeAcademicYear(academicYear || getAcademicYearLabel());
  const classNameKey = classKey(className) || "unknown";
  return `${yearKey}__${classNameKey}`;
}
function statusClass(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Pending") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-700";
}
function initials(name) {
  const p = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!p.length) return "NA";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return `${p[0][0]}${p[1][0]}`.toUpperCase();
}
function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  readOnly = false,
  className = "",
  inputClassName = "",
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ${readOnly ? "bg-gray-100" : ""} ${inputClassName}`}
      />
    </label>
  );
}
function LabeledSelect({ label, value, onChange, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </span>
      <select
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
      >
        {children}
      </select>
    </label>
  );
}
function LabeledTextarea({
  label,
  value,
  onChange,
  rows = 2,
  placeholder = "",
  className = "",
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </span>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      />
    </label>
  );
}
function calc(v) {
  const monthlyPerMonth =
    toNumber(v.monthlyTuitionFee) +
    toNumber(v.monthlyMaintenanceFee) +
    toNumber(v.monthlySportsFee) +
    toNumber(v.monthlyComputerFee);
  const monthlyAnnual = monthlyPerMonth * (toNumber(v.monthlyFeeMonths) || 12);
  const customChargesTotal = Array.isArray(v.customCharges)
    ? v.customCharges.reduce((sum, item) => sum + toNumber(item?.amount), 0)
    : 0;
  const otherTotal =
    toNumber(v.otherAnnualCharge) +
    toNumber(v.otherIdCharge) +
    toNumber(v.otherDiaryCharge) +
    toNumber(v.otherExamFee) +
    customChargesTotal;
  const transportAnnual =
    toNumber(v.transportFeePerMonth) * (toNumber(v.transportMonths) || 11);
  const totalOfAllFees = monthlyAnnual + otherTotal + transportAnnual;
  const prevArrear = toNumber(v.previousYearArrears);
  const arrearPaid = toNumber(v.totalArrearPaidAmount);
  const balanceArrear = prevArrear - arrearPaid;
  const discount = toNumber(v.discountAmount);
  const feePaid = toNumber(v.totalFeePaidAmount);
  const transportPaid = toNumber(v.totalTransportPaidAmount);
  const balanceFee = monthlyAnnual + otherTotal - discount - feePaid;
  const balanceTransportFees = transportAnnual - transportPaid;
  const totalOutstanding = balanceFee + balanceTransportFees + balanceArrear;
  return {
    monthlyAnnual,
    otherTotal,
    customChargesTotal,
    transportAnnual,
    totalOfAllFees,
    prevArrear,
    arrearPaid,
    balanceArrear,
    feePaid,
    transportPaid,
    discount,
    balanceFee,
    balanceTransportFees,
    totalOutstanding,
  };
}
function getLatestPaymentHistoryEntry(paymentHistory) {
  if (!Array.isArray(paymentHistory) || paymentHistory.length === 0) {
    return null;
  }
  return [...paymentHistory].sort(
    (a, b) =>
      new Date(b.recordedAt || b.paymentDate || 0).getTime() -
      new Date(a.recordedAt || a.paymentDate || 0).getTime(),
  )[0];
}

function getTransactionDocumentId(record) {
  const latest = getLatestPaymentHistoryEntry(record?.paymentHistory);
  return String(
    record?.transactionDocumentId ||
      record?.documentTransactionId ||
      latest?.transactionDocumentId ||
      latest?.documentTransactionId ||
      latest?.transactionId ||
      "",
  ).trim();
}

function buildReceiptFromRow(row, sessionYear, student) {
  const latest = getLatestPaymentHistoryEntry(row.paymentHistory);
  const transactionDocumentId = getTransactionDocumentId(row);
  const paymentMode = latest?.paymentMode || row.payment?.paymentMode || "-";
  const discount = toNumber(row.discount);
  const annualChargePaid = Math.max(0, toNumber(row.otherTotal) - discount);
  const feePaidAmount = toNumber(row.feePaid ?? latest?.feePaymentAmount);
  const transportPaidAmount = toNumber(
    row.transportPaid ?? latest?.transportPaymentAmount,
  );
  const arrearPaidAmount = toNumber(
    row.arrearPaid ?? latest?.arrearPaymentAmount,
  );
  const latestMiscPaid = toNumber(latest?.miscPaymentAmount ?? 0);
  const totalAmount =
    feePaidAmount + transportPaidAmount + arrearPaidAmount + latestMiscPaid;

  return {
    receiptNo: latest?.transactionId || row.paymentKey || `RCPT-${row.uid}`,
    date: latest?.paymentDate || row.paymentDate || new Date().toISOString(),
    admissionNo: row.uid || row.admissionNo || row.admissionNumber || "-",
    studentName: row.name || row.studentName || row.childName || "-",
    fatherName: student.father,
    dob: student.dob,
    session: row.payment?.academicYear || row.academicYear || sessionYear,
    className: formatClassLabel(
      row.className || row.class || row.classLabel || "-",
    ),
    installment: row.paymentHistory?.length
      ? `Installment ${row.paymentHistory.length}`
      : "1",
    counterNo: "DPS-RECEIPT",
    transactionDocumentId,
    paymentMode,
    bank:
      paymentMode === "Cheque" || paymentMode === "Bank Transfer"
        ? "HDFC BANK"
        : "-",
    transactionNo: latest?.transactionId || row.paymentKey || "-",
    paymentDate:
      latest?.paymentDate || row.paymentDate || new Date().toISOString(),
    totalAmount,
    feePaidAmount: String(feePaidAmount),
    transportPaidAmount: String(transportPaidAmount),
    arrearPaidAmount: String(arrearPaidAmount),
    customCharges: Array.isArray(row.payment?.customCharges)
      ? row.payment.customCharges
      : [],
    monthlyTuitionFee: toNumber(row.monthlyTuitionFee),
    monthlyMaintenanceFee: toNumber(row.monthlyMaintenanceFee),
    monthlySportsFee: toNumber(row.monthlySportsFee),
    monthlyComputerFee: toNumber(row.monthlyComputerFee),
    monthlyFeeMonths: row.monthlyFeeMonths || 12,
    otherAnnualCharge: toNumber(row.otherAnnualCharge),
    otherIdCharge: toNumber(row.otherIdCharge),
    otherDiaryCharge: toNumber(row.otherDiaryCharge),
    otherExamFee: toNumber(row.otherExamFee),
    transportFeePerMonth: toNumber(row.transportFeePerMonth),
    transportMonths: row.transportMonths || 11,
    discount: discount,
    monthlyAnnual: toNumber(row.monthlyAnnual),
    otherTotal: toNumber(row.otherTotal),
    customChargesTotal: toNumber(row.customChargesTotal),
    transportAnnual: toNumber(row.transportAnnual),
    totalOfAllFees: toNumber(row.totalOfAllFees),
    prevArrear: toNumber(row.prevArrear),
    arrearPaid: toNumber(arrearPaidAmount),
    balanceArrear: toNumber(row.balanceArrear),
    feePaid: toNumber(feePaidAmount),
    transportPaid: toNumber(transportPaidAmount),
    balanceFee: toNumber(row.balanceFee),
    balanceTransportFees: toNumber(row.balanceTransportFees),
    status: row.status || "Pending",
    paymentDetails: {
      student: row.name || row.studentName || row.childName || "-",
      uid: row.uid || row.admissionNo || row.admissionNumber || "-",
      className: formatClassLabel(
        row.className || row.class || row.classLabel || "-",
      ),
      monthlyFees: toNumber(row.monthlyAnnual),
      otherCharges: toNumber(row.otherTotal),
      transportFees: toNumber(row.transportAnnual),
      totalOfAllFees: toNumber(row.totalOfAllFees),
      previousYearArrears: toNumber(row.prevArrear),
      arrearPayment: toNumber(arrearPaidAmount),
      remainingArrear: toNumber(row.balanceArrear),
      paymentAmount: toNumber(feePaidAmount),
      transportAmount: toNumber(transportPaidAmount),
      paymentDate:
        latest?.paymentDate || row.paymentDate || new Date().toISOString(),
      remainingFee: toNumber(row.balanceFee),
      remainingTransportFees: toNumber(row.balanceTransportFees),
      status: row.status || "Pending",
      annualFees: toNumber(row.monthlyAnnual),
      customCharges: toNumber(row.customChargesTotal),
      balanceFee: toNumber(row.balanceFee),
      balanceTransport: toNumber(row.balanceTransportFees),
      balanceArrear: toNumber(row.balanceArrear),
    },
    paymentHistory: Array.isArray(row.paymentHistory) ? row.paymentHistory : [],
    feeItems: [
      {
        slNo: 1,
        description: "Tuition Fee",
        due: row.monthlyAnnual,
        concession: 0,
        paid: feePaidAmount,
      },
      {
        slNo: 2,
        description: "Annual Charges",
        due: row.otherTotal,
        concession: discount,
        paid: annualChargePaid,
      },
      {
        slNo: 3,
        description: "Bus Fee",
        due: row.transportAnnual,
        concession: 0,
        paid: transportPaidAmount,
      },
      {
        slNo: 4,
        description: "Arrear Payment",
        due: row.prevArrear,
        concession: 0,
        paid: arrearPaidAmount,
      },
      {
        slNo: 5,
        description: "Miscellaneous",
        due: latestMiscPaid,
        concession: 0,
        paid: latestMiscPaid,
      },
    ],
    qrValue: `${row.uid}|${row.name}|${latest?.transactionId || row.paymentKey || "receipt"}`,
  };
}

function buildReceiptFromForm(
  selectedStudent,
  formData,
  formComputed,
  historyRows,
  sessionYear,
) {
  console.log("Building receipt from form data...");
  console.log("Form Data:", formData);
  console.log("Form Computed:", formComputed);
  console.log("Selected Student:", selectedStudent);
  console.log("History Rows:", historyRows);
  console.log("Session Year:", sessionYear);
  const paymentDate =
    formData.paymentDate || new Date().toISOString().slice(0, 10);
  const feePaid = toNumber(formData.newFeePaymentAmount);
  const transportPaid = toNumber(formData.newTransportPaymentAmount);
  const arrearPaid = toNumber(formData.newArrearPaymentAmount);
  const miscPaid = toNumber(formData.miscPaymentAmount);
  const discount = toNumber(formData.discountAmount);
  const totalAmount = feePaid + transportPaid + arrearPaid + miscPaid;
  const annualChargePaid = Math.max(0, formComputed.otherTotal - discount);
  const status = formComputed.totalOutstanding <= 0 ? "Paid" : "Pending";

  return {
    console: "Building receipt from form data...",
    receiptNo:
      formData.transactionId ||
      `RCPT-${selectedStudent.uid}-${historyRows.length + 1}`,
    date: paymentDate,
    admissionNo:
      selectedStudent.uid ||
      selectedStudent.admissionNo ||
      selectedStudent.admissionNumber ||
      "-",
    studentName:
      formData.studentName ||
      selectedStudent.name ||
      selectedStudent.studentName ||
      selectedStudent.childName ||
      "-",
    session: formData.academicYear || sessionYear,
    fatherName: formData.fatherName || selectedStudent.fatherName || "N/A",
    className:
      formData.className ||
      formatClassLabel(
        selectedStudent.className ||
          selectedStudent.class ||
          selectedStudent.classLabel ||
          "-",
      ),
    installment: `Installment ${historyRows.length + 1}`,
    counterNo: "DPS-RECEIPT",
    transactionDocumentId: String(formData.transactionDocumentId || "").trim(),
    paymentMode: formData.paymentMode,
    bank:
      formData.paymentMode === "Cheque" ||
      formData.paymentMode === "Bank Transfer"
        ? "HDFC BANK"
        : "-",
    transactionNo: formData.transactionId || "-",
    paymentDate,
    totalAmount,
    customCharges: Array.isArray(formData.customCharges)
      ? formData.customCharges
      : [],
    monthlyTuitionFee: toNumber(formData.monthlyTuitionFee),
    monthlyMaintenanceFee: toNumber(formData.monthlyMaintenanceFee),
    monthlySportsFee: toNumber(formData.monthlySportsFee),
    monthlyComputerFee: toNumber(formData.monthlyComputerFee),
    monthlyFeeMonths: toNumber(formData.monthlyFeeMonths),
    otherAnnualCharge: toNumber(formData.otherAnnualCharge),
    otherIdCharge: toNumber(formData.otherIdCharge),
    otherDiaryCharge: toNumber(formData.otherDiaryCharge),
    otherExamFee: toNumber(formData.otherExamFee),
    transportFeePerMonth: toNumber(formData.transportFeePerMonth),
    transportMonths: toNumber(formData.transportMonths),
    discount: discount,
    monthlyAnnual: toNumber(formComputed.monthlyAnnual),
    otherTotal: toNumber(formComputed.otherTotal),
    customChargesTotal: toNumber(formComputed.customChargesTotal),
    transportAnnual: toNumber(formComputed.transportAnnual),
    totalOfAllFees: toNumber(formComputed.totalOfAllFees),
    prevArrear: toNumber(formComputed.prevArrear),
    arrearPaid: toNumber(arrearPaid),
    balanceArrear: toNumber(formComputed.balanceArrear),
    feePaid: toNumber(feePaid),
    transportPaid: toNumber(transportPaid),
    balanceFee: toNumber(formComputed.balanceFee),
    balanceTransportFees: toNumber(formComputed.balanceTransportFees),
    status,
    currentTransaction: {
      paymentDate,
      paymentMode: formData.paymentMode,
      transactionId: formData.transactionId.trim(),
      feePaymentAmount: feePaid,
      transportPaymentAmount: transportPaid,
      arrearPaymentAmount: arrearPaid,
      miscPaymentAmount: miscPaid,
      miscPaymentRemark: formData.miscPaymentRemark.trim(),
      remarks: formData.remarks.trim(),
      recordedAt: new Date().toISOString(),
    },
    paymentDetails: {
      student:
        formData.studentName ||
        selectedStudent.name ||
        selectedStudent.studentName ||
        selectedStudent.childName ||
        "-",
      uid:
        selectedStudent.uid ||
        selectedStudent.admissionNo ||
        selectedStudent.admissionNumber ||
        "-",
      className:
        formData.className ||
        formatClassLabel(
          selectedStudent.className ||
            selectedStudent.class ||
            selectedStudent.classLabel ||
            "-",
        ),
      monthlyFees: toNumber(formComputed.monthlyAnnual),
      otherCharges: toNumber(formComputed.otherTotal),
      transportFees: toNumber(formComputed.transportAnnual),
      totalOfAllFees: toNumber(formComputed.totalOfAllFees),
      previousYearArrears: toNumber(formComputed.prevArrear),
      arrearPayment: toNumber(arrearPaid),
      remainingArrear: toNumber(formComputed.balanceArrear),
      paymentAmount: toNumber(feePaid),
      transportAmount: toNumber(transportPaid),
      paymentDate,
      remainingFee: toNumber(formComputed.balanceFee),
      remainingTransportFees: toNumber(formComputed.balanceTransportFees),
      status,
      annualFees: toNumber(formComputed.monthlyAnnual),
      customCharges: toNumber(formComputed.customChargesTotal),
      balanceFee: toNumber(formComputed.balanceFee),
      balanceTransport: toNumber(formComputed.balanceTransportFees),
      balanceArrear: toNumber(formComputed.balanceArrear),
    },
    paymentHistory: Array.isArray(historyRows) ? historyRows : [],
    feeItems: [
      {
        slNo: 1,
        description: "Tuition Fee",
        due: formComputed.monthlyAnnual,
        concession: 0,
        paid: feePaid,
      },
      {
        slNo: 2,
        description: "Annual Charges",
        due: formComputed.otherTotal,
        concession: discount,
        paid: annualChargePaid,
      },
      {
        slNo: 3,
        description: "Bus Fee",
        due: formComputed.transportAnnual,
        concession: 0,
        paid: transportPaid,
      },
      {
        slNo: 4,
        description: "Arrear Payment",
        due: formComputed.prevArrear,
        concession: 0,
        paid: arrearPaid,
      },
      {
        slNo: 5,
        description: "Miscellaneous",
        due: miscPaid,
        concession: 0,
        paid: miscPaid,
      },
    ],
    qrValue: `${selectedStudent.uid}|${selectedStudent.name}|${formData.transactionId || paymentDate}`,
  };
}
function buildForm(student, payment, feeStructure, academicYear) {
  const structure = feeStructure || {};
  const pickNumFrom = (keys, fallback = 0) => {
    for (const key of keys) {
      if (payment && hasValue(payment[key])) return toNumber(payment[key]);
      if (structure && hasValue(structure[key]))
        return toNumber(structure[key]);
    }
    return fallback;
  };

  const monthlyFeeMonths = pickNumFrom(
    ["monthlyFeeMonths"],
    DEFAULTS.monthlyFeeMonths,
  );

  const paymentMonthlyBreakup = [
    "monthlyTuitionFee",
    "monthlyMaintenanceFee",
    "monthlySportsFee",
    "monthlyComputerFee",
  ].some((key) => hasValue(payment?.[key]));
  const structureMonthlyBreakup = [
    "monthlyTuitionFee",
    "monthlyMaintenanceFee",
    "monthlySportsFee",
    "monthlyComputerFee",
  ].some((key) => hasValue(structure?.[key]));

  const legacyMonthly = pickNum(payment, ["monthlyFeePerMonth"], 0);
  const legacyAnnual = pickNum(payment, ["monthlyFees"], 0);
  const derivedMonthly =
    legacyMonthly ||
    (legacyAnnual > 0 ? legacyAnnual / (monthlyFeeMonths || 12) : 0);

  const useBreakup = paymentMonthlyBreakup || structureMonthlyBreakup;

  const monthlyTuitionFee = useBreakup
    ? pickNumFrom(["monthlyTuitionFee"], DEFAULTS.monthlyTuitionFee)
    : derivedMonthly || DEFAULTS.monthlyTuitionFee;
  const monthlyMaintenanceFee = useBreakup
    ? pickNumFrom(["monthlyMaintenanceFee"], DEFAULTS.monthlyMaintenanceFee)
    : derivedMonthly
      ? 0
      : DEFAULTS.monthlyMaintenanceFee;
  const monthlySportsFee = useBreakup
    ? pickNumFrom(["monthlySportsFee"], DEFAULTS.monthlySportsFee)
    : derivedMonthly
      ? 0
      : DEFAULTS.monthlySportsFee;
  const monthlyComputerFee = useBreakup
    ? pickNumFrom(["monthlyComputerFee"], DEFAULTS.monthlyComputerFee)
    : derivedMonthly
      ? 0
      : DEFAULTS.monthlyComputerFee;

  const paymentOtherBreakup = [
    "otherAnnualCharge",
    "otherIdCharge",
    "otherDiaryCharge",
    "otherExamFee",
  ].some((key) => hasValue(payment?.[key]));
  const structureOtherBreakup = [
    "otherAnnualCharge",
    "otherIdCharge",
    "otherDiaryCharge",
    "otherExamFee",
  ].some((key) => hasValue(structure?.[key]));

  const legacyOther =
    pickNum(payment, ["otherChargesTotal", "otherCharges"], 0) ||
    pickNum(structure, ["otherChargesTotal"], 0);

  const useOtherBreakup = paymentOtherBreakup || structureOtherBreakup;

  const otherAnnualCharge = useOtherBreakup
    ? pickNumFrom(["otherAnnualCharge"], DEFAULTS.otherAnnualCharge)
    : legacyOther || DEFAULTS.otherAnnualCharge;
  const otherIdCharge = useOtherBreakup
    ? pickNumFrom(["otherIdCharge"], DEFAULTS.otherIdCharge)
    : 0;
  const otherDiaryCharge = useOtherBreakup
    ? pickNumFrom(["otherDiaryCharge"], DEFAULTS.otherDiaryCharge)
    : 0;
  const otherExamFee = useOtherBreakup
    ? pickNumFrom(["otherExamFee"], DEFAULTS.otherExamFee)
    : 0;
  const customCharges = mergeCustomCharges(
    structure?.customCharges,
    payment?.customCharges,
  );

  const transportMonths = pickNumFrom(
    ["transportMonths"],
    DEFAULTS.transportMonths,
  );

  const legacyTransportAnnual =
    pickNum(payment, ["transportFees"], 0) ||
    pickNum(structure, ["transportFees"], 0);
  const transportPerMonth =
    pickNumFrom(["transportFeePerMonth"], 0) ||
    (legacyTransportAnnual > 0
      ? legacyTransportAnnual / (transportMonths || 11)
      : 0) ||
    DEFAULTS.transportFeePerMonth;

  const resolvedAcademicYear = normalizeAcademicYear(
    pick(payment, ["academicYear"], academicYear || structure?.academicYear),
    getAcademicYearLabel(),
  );

  return {
    academicYear: resolvedAcademicYear,
    studentName: String(
      pick(payment, ["studentName", "childName"], student.name),
    ),
    rollNumber: String(pick(payment, ["rollNumber", "rollNo"], student.rollNo)),
    className: formatClassLabel(
      pick(payment, ["className", "childClass"], student.className),
    ),
    profileImageUrl: String(
      pick(payment, ["profileImageUrl"], student.studentImageUrl || ""),
    ),
    monthlyTuitionFee: String(monthlyTuitionFee),
    monthlyMaintenanceFee: String(monthlyMaintenanceFee),
    monthlySportsFee: String(monthlySportsFee),
    monthlyComputerFee: String(monthlyComputerFee),
    monthlyFeeMonths: String(monthlyFeeMonths),
    otherAnnualCharge: String(otherAnnualCharge),
    otherIdCharge: String(otherIdCharge),
    otherDiaryCharge: String(otherDiaryCharge),
    otherExamFee: String(otherExamFee),
    customCharges,
    transportFeePerMonth: String(transportPerMonth),
    transportMonths: String(transportMonths),
    previousYearArrears: String(
      pickNum(payment, ["previousYearArrears", "previousDue"], 0),
    ),
    previousArrearInfo: String(
      pick(payment, ["previousArrearInfo", "previousDueRemarks"], ""),
    ),
    discountAmount: String(
      pickNum(payment, ["discountAmount", "scholarshipAmount"], 0),
    ),
    discountReason: String(
      pick(payment, ["discountReason"], DISCOUNT_REASONS[0]),
    ),
    totalFeePaidAmount: String(
      pickNum(payment, ["feePaidAmount", "paymentAmount", "paidAmount"], 0),
    ),
    totalTransportPaidAmount: String(
      pickNum(payment, ["transportPaidAmount", "transportAmount"], 0),
    ),
    totalArrearPaidAmount: String(pickNum(payment, ["arrearPaidAmount"], 0)),
    newFeePaymentAmount: "",
    newTransportPaymentAmount: "",
    newArrearPaymentAmount: "",
    miscPaymentAmount: "",
    miscPaymentRemark: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMode: String(pick(payment, ["paymentMode"], PAYMENT_MODES[0])),
    transactionId: String(pick(payment, ["transactionId"], "")),
    transactionDocumentId: String(
      pick(
        payment,
        ["transactionDocumentId", "documentTransactionId"],
        getTransactionDocumentId(payment),
      ),
    ),
    remarks: "",
    paymentHistory: Array.isArray(payment?.paymentHistory)
      ? payment.paymentHistory
      : [],
    miscellaneousPayments: Array.isArray(payment?.miscellaneousPayments)
      ? payment.miscellaneousPayments
      : [],
  };
}

function shouldPrefillFromFeeStructure(payment) {
  if (!payment) return true;
  return (
    !Array.isArray(payment.paymentHistory) ||
    payment.paymentHistory.length === 0
  );
}

function academicYearStart(academicYear) {
  const normalized = normalizeAcademicYear(academicYear || "", "");
  const match = String(normalized).match(/^(\d{4})/);
  return match ? Number(match[1]) : 0;
}

function pickLatestFeeStructureByClass(structures, className) {
  const normalizedClassKey = classKey(className);
  if (!normalizedClassKey || !Array.isArray(structures)) return null;
  const matches = structures
    .filter((item) => classKey(item?.className) === normalizedClassKey)
    .sort(
      (a, b) =>
        academicYearStart(b?.academicYear) - academicYearStart(a?.academicYear),
    );
  return matches[0] || null;
}

export default function StudentTable() {
  const [students, setStudents] = useState([]);
  const [paymentByKey, setPaymentByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    student: "",
    uid: "",
    className: "",
    paymentDate: "",
    discountReason: "",
    status: "",
    minFeeAmount: "",
    minBalanceFee: "",
    minBalanceArrear: "",
  });
  const [feeStructures, setFeeStructures] = useState([]);
  const [feeStructureByKey, setFeeStructureByKey] = useState({});
  const [activeAcademicYear, setActiveAcademicYear] = useState(
    normalizeAcademicYear(DEFAULT_ACADEMIC_YEAR, getAcademicYearLabel()),
  );
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [arrearInfo, setArrearInfo] = useState(null);
  const [formTab, setFormTab] = useState("structure");
  const [isStructureSeeded, setIsStructureSeeded] = useState(false);

  useEffect(() => {
    fetchStudentsAndPayments();
  }, []);

  async function fetchStudentsAndPayments() {
    setLoading(true);
    setError("");
    try {
      const [studentSnap, paymentSnap, feeStructureSnap] = await Promise.all([
        getDocs(collection(db, "students")),
        getDocs(collection(db, "received_payment")),
        getDocs(collection(db, "fee_structures")),
      ]);
      const paymentMap = {};
      paymentSnap.docs.forEach((d) => {
        const x = d.data();
        const uid = normalizeUid(x.uid || x.studentUid);
        const className = safeClassName(x.className || x.childClass || x.class);
        if (!uid || !className) return;
        paymentMap[buildPaymentDocId(uid, className)] = { id: d.id, ...x };
      });
      const rows = studentSnap.docs
        .map((d) => {
          const x = d.data();
          return {
            id: d.id,
            uid: normalizeUid(x.uid),
            className: safeClassName(x.className || x.class),
            name: String(x.name || "").trim() || "Unnamed Student",
            rollNo: x.rollNo || x.rollNumber || "",
            studentImageUrl: x.studentImageUrl || x.photoURL || "",
          };
        })
        .filter(
          (s) =>
            s.uid &&
            s.className &&
            !String(s.name).toLowerCase().includes("student"),
        );
      setStudents(rows);
      setPaymentByKey(paymentMap);

      const structureList = feeStructureSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      const structureMap = {};
      structureList.forEach((structure) => {
        const year = normalizeAcademicYear(structure.academicYear);
        const className = safeClassName(structure.className);
        if (!year || !className) return;
        structureMap[buildFeeStructureKey(year, className)] = structure;
      });
      setFeeStructures(structureList);
      setFeeStructureByKey(structureMap);
    } catch (e) {
      console.error(e);
      setError("Unable to load student payment details right now.");
    } finally {
      setLoading(false);
    }
  }

  async function resolveFeeStructureForClass(className) {
    const structureKey = buildFeeStructureKey(activeAcademicYear, className);
    const cached = feeStructureByKey[structureKey] || null;
    if (cached) return cached;

    try {
      const snap = await getDoc(doc(db, "fee_structures", structureKey));
      if (snap.exists()) return { id: snap.id, ...snap.data() };
    } catch (err) {
      console.error("Unable to fetch class fee structure:", err);
    }

    return pickLatestFeeStructureByClass(feeStructures, className);
  }

  const rows = useMemo(() => {
    return students.map((s) => {
      const paymentKey = buildPaymentDocId(s.uid, s.className);
      const payment = paymentByKey[paymentKey] || null;
      const transactionDocumentId = getTransactionDocumentId(payment);
      const structureKey = buildFeeStructureKey(
        activeAcademicYear,
        s.className,
      );
      const feeStructure = feeStructureByKey[structureKey] || null;
      const f = buildForm(s, payment, feeStructure, activeAcademicYear);
      const c = calc(f);
      return {
        ...s,
        paymentKey,
        payment,
        transactionDocumentId,
        discountReason: f.discountReason,
        paymentDate: f.paymentDate,
        previousArrearInfo: f.previousArrearInfo,
        paymentHistory: f.paymentHistory,
        status: payment
          ? c.totalOutstanding <= 0
            ? "Paid"
            : "Pending"
          : "Not Updated",
        ...c,
      };
    });
  }, [students, paymentByKey, activeAcademicYear, feeStructureByKey]);

  const classOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => formatClassLabel(r.className))))
        .filter(Boolean)
        .sort(),
    [rows],
  );
  const discountOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...DISCOUNT_REASONS,
          ...rows.map((r) => r.discountReason).filter((x) => x && x.trim()),
        ]),
      ),
    [rows],
  );
  const availableYears = useMemo(() => {
    const yearSet = new Set(
      feeStructures
        .map((structure) => normalizeAcademicYear(structure.academicYear))
        .filter(Boolean),
    );
    yearSet.add(normalizeAcademicYear(activeAcademicYear));
    return Array.from(yearSet).filter(Boolean).sort().reverse();
  }, [feeStructures, activeAcademicYear]);

  useEffect(() => {
    if (!selectedStudent) return;
    const paymentId = buildPaymentDocId(
      selectedStudent.uid,
      selectedStudent.className,
    );
    const payment = paymentByKey[paymentId] || null;
    const structureKey = buildFeeStructureKey(
      activeAcademicYear,
      selectedStudent.className,
    );
    const feeStructure =
      feeStructureByKey[structureKey] ||
      pickLatestFeeStructureByClass(feeStructures, selectedStudent.className) ||
      null;
    const prefillFromStructure = shouldPrefillFromFeeStructure(payment);
    setIsStructureSeeded(prefillFromStructure);
    setFormData(
      buildForm(
        selectedStudent,
        prefillFromStructure ? null : payment,
        feeStructure,
        activeAcademicYear,
      ),
    );
  }, [
    activeAcademicYear,
    feeStructureByKey,
    feeStructures,
    paymentByKey,
    selectedStudent,
  ]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const minFee = filters.minFeeAmount.trim()
      ? toNumber(filters.minFeeAmount)
      : null;
    const minBal = filters.minBalanceFee.trim()
      ? toNumber(filters.minBalanceFee)
      : null;
    const minArr = filters.minBalanceArrear.trim()
      ? toNumber(filters.minBalanceArrear)
      : null;
    return rows
      .filter((r) => {
        if (q) {
          const ok =
            r.name.toLowerCase().includes(q) ||
            r.uid.toLowerCase().includes(q) ||
            r.className.toLowerCase().includes(q) ||
            formatClassLabel(r.className).toLowerCase().includes(q) ||
            String(r.transactionDocumentId || "")
              .toLowerCase()
              .includes(q);
          if (!ok) return false;
        }
        if (
          filters.student &&
          !r.name.toLowerCase().includes(filters.student.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.uid &&
          !r.uid.toLowerCase().includes(filters.uid.toLowerCase())
        )
          return false;
        if (
          filters.className &&
          formatClassLabel(r.className) !== filters.className
        ) {
          return false;
        }
        if (filters.paymentDate && r.paymentDate !== filters.paymentDate)
          return false;
        if (
          filters.discountReason &&
          r.discountReason !== filters.discountReason
        )
          return false;
        if (filters.status && r.status !== filters.status) return false;
        if (minFee !== null && r.totalOfAllFees < minFee) return false;
        if (minBal !== null && r.balanceFee < minBal) return false;
        if (minArr !== null && r.balanceArrear < minArr) return false;
        return true;
      })
      .sort((a, b) => b.totalOfAllFees - a.totalOfAllFees);
  }, [rows, search, filters]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (a, r) => ({
          monthlyAnnual: a.monthlyAnnual + r.monthlyAnnual,
          otherTotal: a.otherTotal + r.otherTotal,
          transportAnnual: a.transportAnnual + r.transportAnnual,
          totalOfAllFees: a.totalOfAllFees + r.totalOfAllFees,
          prevArrear: a.prevArrear + r.prevArrear,
          arrearPaid: a.arrearPaid + r.arrearPaid,
          balanceArrear: a.balanceArrear + r.balanceArrear,
          feePaid: a.feePaid + r.feePaid,
          transportPaid: a.transportPaid + r.transportPaid,
          balanceFee: a.balanceFee + r.balanceFee,
          balanceTransportFees: a.balanceTransportFees + r.balanceTransportFees,
        }),
        {
          monthlyAnnual: 0,
          otherTotal: 0,
          transportAnnual: 0,
          totalOfAllFees: 0,
          prevArrear: 0,
          arrearPaid: 0,
          balanceArrear: 0,
          feePaid: 0,
          transportPaid: 0,
          balanceFee: 0,
          balanceTransportFees: 0,
        },
      ),
    [filteredRows],
  );

  async function openStudentPaymentForm(student) {
    setSelectedStudent(student);
    setFormLoading(true);
    setSaveMessage("");
    setFormTab("structure");
    setIsStructureSeeded(false);
    try {
      const paymentId = buildPaymentDocId(student.uid, student.className);
      const snap = await getDoc(doc(db, "received_payment", paymentId));
      const payment = snap.exists()
        ? { id: snap.id, ...snap.data() }
        : paymentByKey[paymentId] || null;
      const structureKey = buildFeeStructureKey(
        activeAcademicYear,
        student.className,
      );
      let feeStructure = feeStructureByKey[structureKey] || null;
      const prefillFromStructure = shouldPrefillFromFeeStructure(payment);
      if (prefillFromStructure || !feeStructure) {
        feeStructure = await resolveFeeStructureForClass(student.className);
      }
      setIsStructureSeeded(prefillFromStructure);
      setFormData(
        buildForm(
          student,
          prefillFromStructure ? null : payment,
          feeStructure,
          activeAcademicYear,
        ),
      );
    } catch (e) {
      console.error(e);
      const feeStructure = await resolveFeeStructureForClass(student.className);
      setIsStructureSeeded(true);
      setFormData(buildForm(student, null, feeStructure, activeAcademicYear));
    } finally {
      setFormLoading(false);
    }
  }

  async function savePaymentRecord() {
    if (!selectedStudent || !formData) return;
    if (!formData.remarks || formData.remarks.trim().length < 5) {
      window.alert("Remarks are required and must be at least 5 characters.");
      setSaveMessage("Remarks are required and must be at least 5 characters.");
      return;
    }
    setSaving(true);
    setSaveMessage("");
    try {
      const paymentId = buildPaymentDocId(
        selectedStudent.uid,
        selectedStudent.className,
      );
      const ref = doc(db, "received_payment", paymentId);
      const existingSnap = await getDoc(ref);
      const existing = existingSnap.exists() ? existingSnap.data() : null;
      const structureKey = buildFeeStructureKey(
        activeAcademicYear,
        selectedStudent.className,
      );
      let feeStructure = feeStructureByKey[structureKey] || null;
      const prefillFromStructure = shouldPrefillFromFeeStructure(existing);
      if (prefillFromStructure || !feeStructure) {
        feeStructure = await resolveFeeStructureForClass(
          selectedStudent.className,
        );
      }
      const base = buildForm(
        selectedStudent,
        prefillFromStructure ? null : existing,
        feeStructure,
        activeAcademicYear,
      );
      const nextFee =
        toNumber(base.totalFeePaidAmount) +
        toNumber(formData.newFeePaymentAmount);
      const nextTransport =
        toNumber(base.totalTransportPaidAmount) +
        toNumber(formData.newTransportPaymentAmount);
      const nextArrear =
        toNumber(base.totalArrearPaidAmount) +
        toNumber(formData.newArrearPaymentAmount);
      const paymentDate =
        formData.paymentDate ||
        parseDateValue(existing?.paymentDate) ||
        new Date().toISOString().slice(0, 10);
      const formForCalc = {
        ...formData,
        totalFeePaidAmount: String(nextFee),
        totalTransportPaidAmount: String(nextTransport),
        totalArrearPaidAmount: String(nextArrear),
      };
      const c = calc(formForCalc);
      const currentUserInfo = await getCurrentUserInfo();
      const paymentHistory = [
        ...(Array.isArray(existing?.paymentHistory)
          ? existing.paymentHistory
          : []),
        {
          paymentDate,
          paymentMode: formData.paymentMode,
          transactionId: generateTransactionId(),
          transactionDocumentId: generateTransactionDocumentId({ paymentDate }),
          feePaymentAmount: toNumber(formData.newFeePaymentAmount),
          transportPaymentAmount: toNumber(formData.newTransportPaymentAmount),
          arrearPaymentAmount: toNumber(formData.newArrearPaymentAmount),
          miscPaymentAmount: toNumber(formData.miscPaymentAmount),
          miscPaymentRemark: formData.miscPaymentRemark.trim(),
          remarks: formData.remarks.trim(),
          recordedAt: new Date().toISOString(),
          user: currentUserInfo,
          createdBy: auth.currentUser ? auth.currentUser.uid : null,
        },
      ];
      const misc = [
        ...(Array.isArray(existing?.miscellaneousPayments)
          ? existing.miscellaneousPayments
          : []),
      ];
      if (
        toNumber(formData.miscPaymentAmount) > 0 ||
        formData.miscPaymentRemark.trim()
      ) {
        misc.push({
          amount: toNumber(formData.miscPaymentAmount),
          remark: formData.miscPaymentRemark.trim(),
          paymentDate,
          paymentMode: formData.paymentMode,
          recordedAt: new Date().toISOString(),
        });
      }
      const customCharges = customChargesPayload(formData.customCharges);
      const customChargesTotal = customCharges.reduce(
        (sum, item) => sum + toNumber(item.amount),
        0,
      );
      const payload = {
        uid: selectedStudent.uid,
        className: selectedStudent.className,
        childClass: selectedStudent.className,
        academicYear: formData.academicYear || activeAcademicYear,
        studentName: formData.studentName.trim(),
        childName: formData.studentName.trim(),
        rollNo: formData.rollNumber.trim(),
        rollNumber: formData.rollNumber.trim(),
        profileImageUrl: formData.profileImageUrl.trim(),
        monthlyTuitionFee: toNumber(formData.monthlyTuitionFee),
        monthlyMaintenanceFee: toNumber(formData.monthlyMaintenanceFee),
        monthlySportsFee: toNumber(formData.monthlySportsFee),
        monthlyComputerFee: toNumber(formData.monthlyComputerFee),
        monthlyFeePerMonth:
          toNumber(formData.monthlyTuitionFee) +
          toNumber(formData.monthlyMaintenanceFee) +
          toNumber(formData.monthlySportsFee) +
          toNumber(formData.monthlyComputerFee),
        monthlyFeeMonths: toNumber(formData.monthlyFeeMonths),
        monthlyFees: c.monthlyAnnual,
        otherAnnualCharge: toNumber(formData.otherAnnualCharge),
        otherIdCharge: toNumber(formData.otherIdCharge),
        otherDiaryCharge: toNumber(formData.otherDiaryCharge),
        otherExamFee: toNumber(formData.otherExamFee),
        customCharges,
        customChargesTotal,
        otherChargesTotal: c.otherTotal,
        transportFeePerMonth: toNumber(formData.transportFeePerMonth),
        transportMonths: toNumber(formData.transportMonths),
        transportFees: c.transportAnnual,
        totalOfAllFees: c.totalOfAllFees,
        totalAnnualFees: c.totalOfAllFees,
        previousYearArrears: toNumber(formData.previousYearArrears),
        previousDue: toNumber(formData.previousYearArrears),
        previousArrearInfo: formData.previousArrearInfo.trim(),
        arrearPaidAmount: nextArrear,
        balanceArrear: c.balanceArrear,
        discountAmount: toNumber(formData.discountAmount),
        discountReason: formData.discountReason,
        feePaidAmount: nextFee,
        paidAmount: nextFee,
        paymentAmount: nextFee,
        transportPaidAmount: nextTransport,
        transportAmount: nextTransport,
        balanceFee: c.balanceFee,
        balanceTransportFees: c.balanceTransportFees,
        totalOutstanding: c.totalOutstanding,
        status: c.totalOutstanding <= 0 ? "Paid" : "Pending",
        paymentDate,
        paymentMode: formData.paymentMode,
        transactionId: formData.transactionId.trim(),
        transactionDocumentId:
          paymentHistory[paymentHistory.length - 1]?.transactionDocumentId,
        remarks: formData.remarks.trim(),
        paymentHistory,
        miscellaneousPayments: misc,
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, payload, { merge: true });
      const fresh = await getDoc(ref);
      const freshData = fresh.exists()
        ? { id: fresh.id, ...fresh.data() }
        : payload;
      setPaymentByKey((p) => ({ ...p, [paymentId]: freshData }));
      setFormData(
        buildForm(selectedStudent, freshData, feeStructure, activeAcademicYear),
      );
      setSaveMessage("Payment record saved successfully.");
    } catch (e) {
      console.error(e);
      setSaveMessage("Unable to save payment record.");
    } finally {
      setSaving(false);
    }
  }
  function generateTransactionId() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");

    const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const randomPart = Math.random().toString(36).slice(2, 6).toUpperCase();

    return `TXN-${datePart}-${timePart}-${randomPart}`;
  }
  function updateFilter(k, v) {
    setFilters((p) => ({ ...p, [k]: v }));
  }
  function clearFilters() {
    setSearch("");
    setFilters({
      student: "",
      uid: "",
      className: "",
      paymentDate: "",
      discountReason: "",
      status: "",
      minFeeAmount: "",
      minBalanceFee: "",
      minBalanceArrear: "",
    });
  }

  const formComputed = formData ? calc(formData) : null;
  const modalStudentImage = String(
    formData?.profileImageUrl || selectedStudent?.studentImageUrl || "",
  ).trim();
  const modalStudentName = String(
    formData?.studentName || selectedStudent?.name || "Student",
  ).trim();
  const historyRows = formData
    ? [
        ...(Array.isArray(formData.paymentHistory)
          ? formData.paymentHistory
          : []),
      ].sort(
        (a, b) =>
          new Date(b.recordedAt || b.paymentDate || 0).getTime() -
          new Date(a.recordedAt || a.paymentDate || 0).getTime(),
      )
    : [];

  async function handlePrintRowReceipt(row) {
    try {
      await printReceiptPdf(buildReceiptFromRow(row, activeAcademicYear));
    } catch (error) {
      console.error("Unable to print receipt:", error);
      setError("Unable to generate the receipt right now.");
    }
  }

  async function handlePrintFormReceipt() {
    if (!selectedStudent || !formData || !formComputed) return;
    try {
      await printReceiptPdf(
        buildReceiptFromForm(
          selectedStudent,
          formData,
          formComputed,
          historyRows,
          activeAcademicYear,
        ),
      );
    } catch (error) {
      console.error("Unable to print receipt:", error);
      setSaveMessage("Unable to generate the receipt right now.");
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm mt-6 p-4 sm:p-6 border border-gray-200">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            Student Accounts and Fees
          </h3>
          <p className="text-sm text-gray-500">
            Filtered totals update automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={activeAcademicYear}
            onChange={(e) => setActiveAcademicYear(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            title="Academic Year"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search name / UID / class"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 max-w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={fetchStudentsAndPayments}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        <input
          value={filters.student}
          onChange={(e) => updateFilter("student", e.target.value)}
          placeholder="Filter student"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <input
          value={filters.uid}
          onChange={(e) => updateFilter("uid", e.target.value)}
          placeholder="Filter UID"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={filters.className}
          onChange={(e) => updateFilter("className", e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All classes</option>
          {classOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={filters.paymentDate}
          onChange={(e) => updateFilter("paymentDate", e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={filters.discountReason}
          onChange={(e) => updateFilter("discountReason", e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All discount reasons</option>
          {discountOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => updateFilter("status", e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
          <option value="Not Updated">Not Updated</option>
        </select>
        <input
          type="number"
          value={filters.minFeeAmount}
          onChange={(e) => updateFilter("minFeeAmount", e.target.value)}
          placeholder="Fees >= amount"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="number"
          value={filters.minBalanceFee}
          onChange={(e) => updateFilter("minBalanceFee", e.target.value)}
          placeholder="Balance fee >= amount"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-4 text-sm font-medium text-gray-700">
        Filtered totals: {filteredRows.length} students | Total fees{" "}
        {money(totals.totalOfAllFees)} | Balance fee {money(totals.balanceFee)}{" "}
        | Balance transport {money(totals.balanceTransportFees)}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-[1800px] w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-3 text-left">Student</th>
              <th className="px-3 py-3 text-left">UID</th>
              <th className="px-3 py-3 text-left">Class</th>
              <th className="px-3 py-3 text-left">Monthly Fees</th>
              <th className="px-3 py-3 text-left">Other Charges</th>
              <th className="px-3 py-3 text-left">Transport Fees</th>
              <th className="px-3 py-3 text-left">Total of All Fees</th>
              <th className="px-3 py-3 text-left">Previous Year Arrears</th>
              <th className="px-3 py-3 text-left">Arrear Payment</th>
              <th className="px-3 py-3 text-left">Remaing Arrear</th>
              <th className="px-3 py-3 text-left">Payment Amount</th>
              <th className="px-3 py-3 text-left">Transport Amount</th>
              <th className="px-3 py-3 text-left">Payment Date</th>
              <th className="px-3 py-3 text-left">Remaing Fee</th>
              <th className="px-3 py-3 text-left"> Remaing Transport Fees</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading && (
              <tr>
                <td
                  colSpan={17}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  Loading student fee records...
                </td>
              </tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={17}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No students matched your filters.
                </td>
              </tr>
            )}
            {!loading &&
              filteredRows.map((r) => (
                <tr
                  key={`${r.uid}-${r.className}`}
                  className="hover:bg-indigo-50/40 transition cursor-pointer"
                  onClick={() => openStudentPaymentForm(r)}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {r.studentImageUrl ? (
                        <img
                          src={r.studentImageUrl}
                          alt={r.name}
                          className="w-9 h-9 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold text-xs flex items-center justify-center">
                          {initials(r.name)}
                        </div>
                      )}
                      <span className="font-semibold text-gray-900">
                        {r.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-gray-700">
                    {r.uid}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {formatClassLabel(r.className)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.monthlyAnnual)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.otherTotal)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.transportAnnual)}
                  </td>
                  <td className="px-3 py-3 font-semibold text-gray-900">
                    {money(r.totalOfAllFees)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      <span>{money(r.prevArrear)}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setArrearInfo(r);
                        }}
                        className="w-5 h-5 rounded-full border border-indigo-300 text-indigo-700 text-xs font-semibold hover:bg-indigo-50"
                      >
                        i
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.arrearPaid)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.balanceArrear)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.feePaid)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.transportPaid)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {formatDate(r.paymentDate)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.balanceFee)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {money(r.balanceTransportFees)}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusClass(r.status)}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openStudentPaymentForm(r);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      >
                        Update
                      </button>
                      <NewReceiptPrintButton
                        buildReceipt={async () => {
                          const student = await getStudentByUid(r.uid);
                          console.log(
                            "Printing receipt for student:",
                            student.dob,
                          );

                          return buildReceiptFromRow(
                            r,
                            activeAcademicYear,
                            student || "N/A",
                          );
                        }}
                      />
                      {/* <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintRowReceipt(r);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                      >
                        Print Receipt
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
          <tfoot className="bg-indigo-50 border-t border-indigo-100">
            <tr className="font-semibold text-indigo-900">
              <td className="px-3 py-3" colSpan={3}>
                Filtered Totals
              </td>
              <td className="px-3 py-3">{money(totals.monthlyAnnual)}</td>
              <td className="px-3 py-3">{money(totals.otherTotal)}</td>
              <td className="px-3 py-3">{money(totals.transportAnnual)}</td>
              <td className="px-3 py-3">{money(totals.totalOfAllFees)}</td>
              <td className="px-3 py-3">{money(totals.prevArrear)}</td>
              <td className="px-3 py-3">{money(totals.arrearPaid)}</td>
              <td className="px-3 py-3">{money(totals.balanceArrear)}</td>
              <td className="px-3 py-3">{money(totals.feePaid)}</td>
              <td className="px-3 py-3">{money(totals.transportPaid)}</td>
              <td className="px-3 py-3">-</td>
              <td className="px-3 py-3">{money(totals.balanceFee)}</td>
              <td className="px-3 py-3">
                {money(totals.balanceTransportFees)}
              </td>
              <td className="px-3 py-3">-</td>
              <td className="px-3 py-3">-</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {selectedStudent && (
        <div className="fixed inset-0 z-50 bg-black/45 p-4 overflow-y-auto">
          <div className="max-w-7xl mx-auto mt-6 mb-10 bg-white rounded-2xl shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                {modalStudentImage ? (
                  <img
                    src={modalStudentImage}
                    alt={modalStudentName}
                    className="h-12 w-12 rounded-xl object-cover border border-indigo-200 bg-white"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-xl border border-indigo-200 bg-white text-indigo-700 font-semibold text-sm flex items-center justify-center">
                    {initials(modalStudentName)}
                  </div>
                )}
                <div>
                  <h4 className="text-xl font-bold text-gray-900">
                    Update Student Account
                  </h4>
                  <p className="text-sm text-gray-600">
                    Academic Year: {activeAcademicYear} | UID:{" "}
                    {selectedStudent.uid} | Class:{" "}
                    {formatClassLabel(selectedStudent.className)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedStudent(null);
                  setFormData(null);
                  setSaveMessage("");
                  setFormTab("structure");
                  setIsStructureSeeded(false);
                }}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-6 space-y-4">
              {formLoading || !formData || !formComputed ? (
                <div className="py-10 text-center text-gray-500">
                  Loading payment form...
                </div>
              ) : (
                <>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                    <button
                      type="button"
                      onClick={() => setFormTab("structure")}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${
                        formTab === "structure"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Pay Structure
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormTab("other")}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${
                        formTab === "other"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Fee Payment
                    </button>
                  </div>

                  {isStructureSeeded && (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                      No payment history found for this student. Values are
                      prefilled from the selected class fee structure.
                    </div>
                  )}

                  {formTab === "structure" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <LabeledInput
                          label="Student name"
                          value={formData.studentName}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              studentName: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Roll number"
                          value={formData.rollNumber}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              rollNumber: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="UID"
                          value={selectedStudent.uid}
                          readOnly
                        />
                        <LabeledInput
                          label="Class"
                          value={formData.className}
                          readOnly
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <LabeledInput
                          label="Tuition / month"
                          type="number"
                          value={formData.monthlyTuitionFee}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              monthlyTuitionFee: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Maintenance / month"
                          type="number"
                          value={formData.monthlyMaintenanceFee}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              monthlyMaintenanceFee: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Sports / month"
                          type="number"
                          value={formData.monthlySportsFee}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              monthlySportsFee: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Computer / month"
                          type="number"
                          value={formData.monthlyComputerFee}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              monthlyComputerFee: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Monthly months"
                          type="number"
                          value={formData.monthlyFeeMonths}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              monthlyFeeMonths: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <LabeledInput
                          label="Annual charge"
                          type="number"
                          value={formData.otherAnnualCharge}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              otherAnnualCharge: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="ID charge"
                          type="number"
                          value={formData.otherIdCharge}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              otherIdCharge: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Diary charge"
                          type="number"
                          value={formData.otherDiaryCharge}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              otherDiaryCharge: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Exam fee"
                          type="number"
                          value={formData.otherExamFee}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              otherExamFee: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Transport / month"
                          type="number"
                          value={formData.transportFeePerMonth}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              transportFeePerMonth: e.target.value,
                            }))
                          }
                        />
                      </div>

                      {/* <div className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-800">
                          Custom Charges
                        </h5>
                        <p className="text-xs text-gray-500">
                          Defined in the fee structure for this class/year.
                        </p>
                      </div>
                    </div>
                    {(!formData.customCharges ||
                      formData.customCharges.length === 0) && (
                      <p className="text-xs text-gray-500">
                        No custom charges configured for this class.
                      </p>
                    )}
                    {formData.customCharges &&
                      formData.customCharges.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {formData.customCharges.map((charge, index) => (
                            <LabeledInput
                              key={`${charge.label}-${index}`}
                              label={
                                charge.label || `Custom charge ${index + 1}`
                              }
                              type="number"
                              value={charge.amount}
                              onChange={(e) =>
                                updateCustomChargeAmount(index, e.target.value)
                              }
                            />
                          ))}
                        </div>
                      )}
                  </div> */}
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2">
                        <p className="text-sm text-indigo-800">
                          After updating pay structure, open the Fee Payment tab
                          to add remarks and save.
                        </p>
                        <button
                          type="button"
                          onClick={() => setFormTab("other")}
                          className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-white"
                        >
                          Go to Fee Payment
                        </button>
                      </div>
                    </>
                  )}

                  {formTab === "other" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <LabeledInput
                          label="Transport months"
                          type="number"
                          value={formData.transportMonths}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              transportMonths: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Previous arrears"
                          type="number"
                          value={formData.previousYearArrears}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              previousYearArrears: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Discount amount"
                          type="number"
                          value={formData.discountAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              discountAmount: e.target.value,
                            }))
                          }
                        />
                        <LabeledSelect
                          label="Discount reason"
                          value={formData.discountReason}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              discountReason: e.target.value,
                            }))
                          }
                        >
                          {discountOptions.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </LabeledSelect>
                        <LabeledInput
                          label="Payment date"
                          type="date"
                          value={formData.paymentDate}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              paymentDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <LabeledInput
                          label="Total fee paid"
                          value={formData.totalFeePaidAmount}
                          readOnly
                        />
                        <LabeledInput
                          label="Total transport paid"
                          value={formData.totalTransportPaidAmount}
                          readOnly
                        />
                        <LabeledInput
                          label="Total arrear paid"
                          value={formData.totalArrearPaidAmount}
                          readOnly
                        />
                        <LabeledSelect
                          label="Payment mode"
                          value={formData.paymentMode}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              paymentMode: e.target.value,
                            }))
                          }
                        >
                          {PAYMENT_MODES.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </LabeledSelect>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <LabeledInput
                          label="New fee payment"
                          type="number"
                          value={formData.newFeePaymentAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              newFeePaymentAmount: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="New transport payment"
                          type="number"
                          value={formData.newTransportPaymentAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              newTransportPaymentAmount: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="New arrear payment"
                          type="number"
                          value={formData.newArrearPaymentAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              newArrearPaymentAmount: e.target.value,
                            }))
                          }
                        />
                        <LabeledInput
                          label="Transaction ID"
                          value={formData.transactionId}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              transactionId: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <LabeledTextarea
                          label="Previous arrear info"
                          value={formData.previousArrearInfo}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              previousArrearInfo: e.target.value,
                            }))
                          }
                          placeholder="Shown in the arrear info popup"
                        />
                        <LabeledTextarea
                          label="Misc payment remark"
                          value={formData.miscPaymentRemark}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              miscPaymentRemark: e.target.value,
                            }))
                          }
                          placeholder="Optional note for misc payment"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <LabeledInput
                          label="Misc payment amount"
                          type="number"
                          value={formData.miscPaymentAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              miscPaymentAmount: e.target.value,
                            }))
                          }
                        />
                        <LabeledTextarea
                          label="Remarks (required)"
                          value={formData.remarks}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              remarks: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="text-sm text-gray-700">
                        Totals: Annual fees {money(formComputed.totalOfAllFees)}{" "}
                        | Custom charges{" "}
                        {money(formComputed.customChargesTotal)} | Balance fee{" "}
                        {money(formComputed.balanceFee)} | Balance transport{" "}
                        {money(formComputed.balanceTransportFees)} | Balance
                        arrear {money(formComputed.balanceArrear)}
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left">Date</th>
                              <th className="px-3 py-2 text-left">Mode</th>
                              <th className="px-3 py-2 text-left">Fee</th>
                              <th className="px-3 py-2 text-left">Transport</th>
                              <th className="px-3 py-2 text-left">Arrear</th>
                              <th className="px-3 py-2 text-left">Misc</th>
                              <th className="px-3 py-2 text-left">Remarks</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {historyRows.length === 0 && (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="px-3 py-4 text-center text-gray-500"
                                >
                                  No payment history yet.
                                </td>
                              </tr>
                            )}
                            {historyRows.map((h, i) => (
                              <tr key={`${h.recordedAt || h.paymentDate}-${i}`}>
                                <td className="px-3 py-2">
                                  {formatDate(parseDateValue(h.paymentDate))}
                                </td>
                                <td className="px-3 py-2">
                                  {h.paymentMode || "-"}
                                </td>
                                <td className="px-3 py-2">
                                  {money(h.feePaymentAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  {money(h.transportPaymentAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  {money(h.arrearPaymentAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  {money(h.miscPaymentAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  {h.remarks || h.miscPaymentRemark || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-indigo-700">{saveMessage}</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={savePaymentRecord}
                            disabled={saving}
                            className="rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 px-5 py-2 disabled:opacity-60"
                          >
                            {saving ? "Saving..." : "Add Payment"}
                          </button>
                          {/* <button
                            type="button"
                            onClick={handlePrintFormReceipt}
                            className="rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 px-5 py-2"
                          >
                            Print Receipt
                          </button> */}
                          <NewReceiptPrintButton
                            buildReceipt={() =>
                              buildReceiptFromForm(
                                selectedStudent,
                                formData,
                                formComputed,
                                historyRows,
                                activeAcademicYear,
                              )
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {arrearInfo && (
        <div className="fixed inset-0 z-[70] bg-black/50 p-4 overflow-y-auto">
          <div className="max-w-2xl mx-auto mt-12 bg-white rounded-xl border border-gray-200 shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-gray-900">
                  Previous Arrear Details
                </h4>
                <p className="text-sm text-gray-600">
                  {arrearInfo.name} ({arrearInfo.uid}) -{" "}
                  {formatClassLabel(arrearInfo.className)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setArrearInfo(null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p>
                <span className="font-semibold">Previous Arrears:</span>{" "}
                {money(arrearInfo.prevArrear)}
              </p>
              <p>
                <span className="font-semibold">Arrear Paid:</span>{" "}
                {money(arrearInfo.arrearPaid)}
              </p>
              <p>
                <span className="font-semibold">Balance Arrear:</span>{" "}
                {money(arrearInfo.balanceArrear)}
              </p>
              <p className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                {arrearInfo.previousArrearInfo ||
                  "No additional arrear notes available."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
